import React, { useEffect, useState } from 'react';
import {
    Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography
} from "@mui/material";
import { LoadingButton } from '@mui/lab';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import Swal from 'sweetalert2';
import { defaultSwalPreferences } from 'app/utils/appHelpers';
import userServices from 'app/services/user-services';
import Div from "@jumbo/shared/Div";
import DriverCostFields from 'app/pages/users/DriverCostFields';

const UserCreateModal = props => {
    const { isOpen, onClose = () => {}, onSave = () => {}, onDriverLimitReached = () => {} } = props;
    const [saving, setSaving] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [profileOptions, setProfileOptions] = useState([]);
    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_primary: '',
        password: '',
        profile: '',
        routing_settings: {}
    });

    useEffect(() => {
        if (isOpen) {
            setForm({ first_name: '', last_name: '', email: '', phone_primary: '', password: '', profile: '', routing_settings: {} });
            setLoadingOptions(true);
            userServices.getEditOptions()
                .then(resp => setProfileOptions(resp?.profile_names || []))
                .catch(() => {})
                .finally(() => setLoadingOptions(false));
        }
    }, [isOpen]);

    const handleChange = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    const phoneInvalid = Boolean(form.phone_primary.trim()) && !/^[0-9]{9,11}$/.test(form.phone_primary.trim());

    const isValid = () => form.first_name.trim() && form.last_name.trim() && form.email.trim() && form.password.trim() && form.profile && !phoneInvalid;

    const handleSave = () => {
        if (!isValid()) return;

        if (phoneInvalid) {
            Swal.mixin(defaultSwalPreferences(Swal)).fire({
                icon: 'warning',
                title: 'Teléfono inválido',
                text: 'El teléfono debe tener entre 9 y 11 dígitos (solo números).',
            });
            return;
        }

        setSaving(true);

        const body = {
            user: {
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                email: form.email.trim(),
                phone_primary: form.phone_primary.trim() || undefined,
                password: form.password,
                password_confirmation: form.password,
                profiles: [{ name: form.profile }],
                // Solo si es conductor: para el resto de los perfiles el costeo no lo mira.
                ...(form.profile === 'driver' ? { routing_settings: form.routing_settings || {} } : {})
            }
        };

        userServices.createUser(body)
            .then(resp => {
                Swal.mixin(defaultSwalPreferences(Swal)).fire({
                    icon: 'success',
                    title: `Usuario ${form.first_name} ${form.last_name} creado exitosamente`,
                });
                onSave(resp);
                onClose();
            })
            // El interceptor global de jwtAuth muestra la causa del error, salvo el
            // limite del plan, que lo deja pasar para que la pantalla muestre su modal.
            .catch((error) => {
                const data = error?.response?.data;
                if (data?.error_code === 'subscription_limit_reached' && data?.feature === 'usuariosMobile') {
                    onDriverLimitReached(data);
                }
            })
            .finally(() => setSaving(false));
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={isOpen} onClose={onClose}>
            <DialogTitle>
                <Typography variant="h2" color="primary">Crear usuario</Typography>
            </DialogTitle>
            <DialogContent>
                {loadingOptions ? (
                    <Div sx={{ textAlign: 'center', py: 3 }}>
                        <CircularProgress />
                    </Div>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Nombre *"
                                value={form.first_name}
                                onChange={handleChange('first_name')}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Apellido *"
                                value={form.last_name}
                                onChange={handleChange('last_name')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Correo electrónico *"
                                type="email"
                                value={form.email}
                                onChange={handleChange('email')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Teléfono (opcional)"
                                value={form.phone_primary}
                                onChange={handleChange('phone_primary')}
                                error={phoneInvalid}
                                helperText={phoneInvalid ? 'Debe tener entre 9 y 11 dígitos (solo números)' : 'Formato: 56912345678'}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Perfil *</InputLabel>
                                <Select
                                    value={form.profile}
                                    label="Perfil *"
                                    onChange={handleChange('profile')}
                                >
                                    {profileOptions.map(p => (
                                        <MenuItem key={p.name} value={p.name}>{p.human_name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Contraseña temporal *"
                                type="password"
                                value={form.password}
                                onChange={handleChange('password')}
                            />
                        </Grid>
                        {/* Solo para conductores: es con lo que se costean sus viajes. */}
                        { form.profile === 'driver' &&
                            <Grid item xs={12}>
                                <DriverCostFields
                                    settings={form.routing_settings || {}}
                                    onChange={(routing_settings) => setForm({ ...form, routing_settings })}
                                />
                            </Grid>
                        }
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
                <LoadingButton
                    loading={saving}
                    variant="contained"
                    color="success"
                    size="small"
                    endIcon={<SaveOutlinedIcon />}
                    loadingPosition="end"
                    disabled={!isValid()}
                    onClick={handleSave}
                >
                    {saving ? 'Guardando' : 'Guardar'}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default UserCreateModal;
